import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Modal, Progress, Space, Spin, Tag, Typography } from 'antd';
import api from '../services/api';
import { TrainingVideo } from '../types';
import { usePlatformGuide } from './PlatformGuide';
import './AdminVideoSubtitlesModal.css';

type Language = 'ru' | 'en';
interface SubtitleJob {
  id: string; file_revision: string; languages: Language[];
  status: 'queued' | 'running' | 'completed' | 'failed' | 'stale';
  progress_percent: number; completed_languages: Language[]; error: string | null;
}
interface GenerationState {
  enabled: boolean; available_languages: Language[]; job: SubtitleJob | null; unavailable_reason: string | null;
}
const labels: Record<Language, string> = { ru: 'Русский', en: 'English' };
const active = (job?: SubtitleJob | null) => job?.status === 'queued' || job?.status === 'running';
const reasons: Record<string, string> = {
  disabled: 'Автогенерация пока не включена на этом сервере.',
  model_unavailable: 'Модель распознавания ещё не установлена. Обратитесь к администратору платформы.',
  dependency_unavailable: 'Движок распознавания ещё не установлен.',
  python_unavailable: 'Движок распознавания временно недоступен.',
  recognizer_unavailable: 'Движок распознавания временно недоступен.',
  decoder_unavailable: 'Сервис обработки звука ещё не настроен.',
  source_unavailable: 'Исходное видео недоступно. Обновите список материалов.',
  file_changed: 'Видео было заменено. Обновите список материалов и запустите генерацию для нового файла.',
  generation_in_progress: 'Для этого видео уже выполняется задача. Дождитесь её завершения.',
  queue_full: 'Очередь заполнена. Попробуйте позже.',
  interrupted: 'Обработка прервана перезапуском сервиса. Можно запустить её повторно.',
  generation_timeout: 'Обработка заняла слишком много времени. Попробуйте повторно.',
  no_speech: 'В записи не удалось распознать речь.',
};
const reasonText = (code?: string | null) => (code && reasons[code]) || 'Не удалось обработать субтитры. Попробуйте повторно или обратитесь к администратору.';

/** Explicit admin action; merely opening this dialog never creates a job. */
const AdminVideoSubtitlesModal: React.FC<{
  video: TrainingVideo; onClose: () => void; onComplete?: () => void;
}> = ({ video, onClose, onComplete }) => {
  const { openGuide } = usePlatformGuide();
  const [state, setState] = useState<GenerationState | null>(null);
  const [selected, setSelected] = useState<Language[]>(['ru', 'en']);
  const [error, setError] = useState('');
  const [queryError, setQueryError] = useState('');
  const [posting, setPosting] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const mounted = useRef(true);
  const submitting = useRef(false);
  const chosen = useRef(false);
  const completed = useRef(new Set<string>());
  const completeCallback = useRef(onComplete);
  completeCallback.current = onComplete;
  const endpoint = `/video/admin/videos/${video.id}/subtitle-generation`;
  const accept = (data: GenerationState) => {
    setState(data);
    if (!chosen.current) setSelected((['ru', 'en'] as Language[]).filter(lang => !data.available_languages.includes(lang)));
    // Previously created tracks can arrive on initial load; notify a playing preview too.
    if (data.job?.status === 'completed' && data.job.file_revision === video.file_revision && !completed.current.has(data.job.id)) {
      completed.current.add(data.job.id); completeCallback.current?.();
    }
  };
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const currentJob = state?.job;
    let wasActive = !!currentJob && currentJob.file_revision === video.file_revision && active(currentJob);
    const poll = async () => {
      try {
        const { data } = await api.get<GenerationState>(endpoint, { signal: controller.signal });
        if (controller.signal.aborted) return;
        accept(data); setQueryError(''); wasActive = data.job?.file_revision === video.file_revision && active(data.job);
        if (wasActive) timer = setTimeout(poll, 2500);
      } catch {
        if (!controller.signal.aborted) {
          setQueryError('Не удалось получить состояние обработки.');
          if (wasActive) timer = setTimeout(poll, 5000);
        }
      }
    };
    void poll();
    return () => { controller.abort(); clearTimeout(timer); };
    // A new explicit submission restarts polling without remounting the video.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, video.file_revision, refresh]);

  const job = state?.job && state.job.file_revision === video.file_revision ? state.job : null;
  const missing = selected.filter(lang => !state?.available_languages.includes(lang));
  const busy = active(job);
  const ready = state?.available_languages.length === 2;
  const unavailable = state && (!state.enabled || state.unavailable_reason);
  const generate = async () => {
    if (submitting.current || busy || !missing.length || !video.file_revision || unavailable) return;
    submitting.current = true; setPosting(true); setError('');
    try {
      const { data } = await api.post<SubtitleJob>(endpoint, { languages: missing, expected_file_revision: video.file_revision });
      if (!mounted.current) return;
      setState(current => current && ({ ...current, job: data })); setRefresh(value => value + 1);
    } catch (err) {
      if (mounted.current) {
        const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
        setError(reasonText(typeof detail === 'string' ? detail : null));
        setRefresh(value => value + 1);
      }
    } finally {
      submitting.current = false;
      if (mounted.current) setPosting(false);
    }
  };
  return <Modal title="Субтитры к видео" open width={620} className="video-subtitles-modal" maskClosable={false}
    onCancel={() => !posting && onClose()} closable={!posting} footer={
      <Space wrap>
        <Button onClick={openGuide}>Инструкция</Button>
        <Button disabled={posting} onClick={onClose}>Закрыть</Button>
        <Button data-guide="video-subtitles-generate" type="primary" loading={posting} disabled={!state || !!unavailable || busy || ready || !missing.length || !video.file_revision} onClick={generate}>
          {busy ? 'Обработка выполняется' : ready ? 'Обе дорожки готовы' : 'Создать субтитры'}
        </Button>
      </Space>}>
    <div data-guide="video-subtitles-modal">
    <Typography.Paragraph strong>{video.title}</Typography.Paragraph>
    <Typography.Paragraph type="secondary">Речь на русском языке: создайте расшифровку или перевод на английский. Готовые дорожки сохраняются.</Typography.Paragraph>
    {!state && !error && !queryError && <Spin aria-label="Загрузка состояния субтитров" />}
    {(error || queryError) && <Alert type="error" showIcon message={error || queryError} action={<Button size="small" onClick={() => { setError(''); setRefresh(value => value + 1); }}>Обновить</Button>} />}
    {unavailable && <Alert type="info" showIcon message={reasonText(state?.enabled ? state.unavailable_reason : 'disabled')} />}
    {state && <>
      <div className="video-subtitles-languages" data-guide="video-subtitles-languages" role="group" aria-label="Языки субтитров">
        <Checkbox.Group value={selected} disabled={busy || posting || !!unavailable} onChange={values => { chosen.current = true; setSelected(values as Language[]); }}>
          {(['ru', 'en'] as Language[]).map(lang => <Checkbox key={lang} value={lang} disabled={state.available_languages.includes(lang)}>
            {lang === 'ru' ? 'Русский — расшифровка' : 'English — перевод'}
            {state.available_languages.includes(lang) && <Tag color="success">Готово</Tag>}
          </Checkbox>)}
        </Checkbox.Group>
      </div>
      <section data-guide="video-subtitles-status" aria-label="Состояние субтитров">
      {state.available_languages.length > 0 && <Typography.Paragraph>Доступно в плеере: {state.available_languages.map(lang => labels[lang]).join(', ')}.</Typography.Paragraph>}
      {busy && <div aria-live="polite">
        <Typography.Paragraph>{job?.status === 'queued' ? 'В очереди на обработку' : 'Распознаём речь и создаём субтитры'}</Typography.Paragraph>
        <Progress percent={job?.progress_percent || 0} status="active" />
        <Typography.Paragraph type="secondary">Окно можно закрыть. Задача продолжится в фоне; длительная запись может обрабатываться несколько часов.</Typography.Paragraph>
      </div>}
      {job?.status === 'completed' && <Alert type="success" showIcon message="Субтитры готовы. Выберите язык в плеере." />}
      {(job?.status === 'failed' || job?.status === 'stale') && <Alert type="warning" showIcon message={reasonText(job.error)} />}
      </section>
      <Typography.Paragraph type="secondary" className="video-subtitles-quality-note">После распознавания проверьте названия и артикулы: автоматические субтитры могут содержать ошибки.</Typography.Paragraph>
    </>}
    </div>
  </Modal>;
};

export default AdminVideoSubtitlesModal;
